import type * as ents from '@attain-apps/entities'
import type * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'
import _uniq from 'lodash-es/uniq'

/**
 * TODO: explain what this enum is, when it is used and why, as well as any examples
 *
 * TODO: add JSDoc comments to each enum value to explain it
 */
export enum Id {
  IdcCategory = 'idcCategory',
  IdcSubcategory = 'idcSubcategory',
  ExclusionLabel = 'exclusionLabel',
  AdjustmentLabel = 'adjustmentLabel',
}
// Needs to be before namespace is defined
const IDS: ReadonlySet<Id> = new Set(Object.values(Id) as Id[])
export namespace Id {
  export const ALL: readonly Id[] = Array.from(IDS)
  export const isId = TypeChecker.Factory.oneOf(IDS)
  export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)
}

/* ------------------------------- Attributes ------------------------------- */

export function getIdcCategoryAttribute<TEntity>(
  getMapping: (entity: TEntity) => ents.extra.rate.gl.Mapping | undefined,
): ec.models.Attribute<ec.models.TypeParams.WithDefaults<{ entity: TEntity }>> {
  return {
    /**
     * Returns unique list of {@link ents.extra.common.FaFunction.Category.Id}
     * that appear in this entity
     */

    getValueFrom: entity =>
      _uniq(
        getMapping(entity)?.entries.map(entry =>
          getIdcCategoryValue(entry.faFunctionCategorization),
        ),
      ),
    id: Id.IdcCategory,
    name: 'IDC Code',
    isSortable: true,
  }
}

export function getIdcSubcategoryAttribute<TEntity>(
  getMapping: (entity: TEntity) => ents.extra.rate.gl.Mapping | undefined,
): ec.models.Attribute<ec.models.TypeParams.WithDefaults<{ entity: TEntity }>> {
  return {
    /**
     * Returns unique list of
     * {@link ents.extra.common.FaFunction.Subcategory.Id} that appear in this
     * entity
     */
    getValueFrom: entity =>
      _uniq(
        getMapping(entity)
          ?.entries.map(entry =>
            getIdcSubcategoryValue(entry.faFunctionCategorization),
          )
          // Filter out undefined values to ensure the list contains only
          // valid subcategories.
          .filter(TypeChecker.isNotNullish.raw),
      ),
    id: Id.IdcSubcategory,
    name: 'IDC Subcode',
    isSortable: true,
  }
}

export function getExclusionLabelAttribute<TEntity>(
  getMapping: (entity: TEntity) => ents.extra.rate.gl.Mapping | undefined,
): ec.models.Attribute<ec.models.TypeParams.WithDefaults<{ entity: TEntity }>> {
  return {
    /**
     * Returns unique list of exclusion labels that appear in this entity
     */
    getValueFrom: entity =>
      _uniq(getMapping(entity)?.entries.map(entry => entry.exclusionLabel)),
    id: Id.ExclusionLabel,
    name: 'Exclusion Label',
    isSortable: true,
  }
}

export function getAdjustmentLabelAttribute<TEntity>(
  getMapping: (entity: TEntity) => ents.extra.rate.gl.Mapping | undefined,
): ec.models.Attribute<ec.models.TypeParams.WithDefaults<{ entity: TEntity }>> {
  return {
    // Local table sorting compares extracted values directly. Nullish labels
    // compare equal to strings, so normalize absence to a text value here.
    getValueFrom: entity => getMapping(entity)?.adjustmentLabel ?? '',
    id: Id.AdjustmentLabel,
    name: 'Adjustment Label',
    isSortable: true,
  }
}

/* ----------------------------- Attribute Lists ---------------------------- */

export function getIdcCategorizationAttributes<TEntity>(
  getMapping: (entity: TEntity) => ents.extra.rate.gl.Mapping | undefined,
): readonly ec.models.Attribute<
  ec.models.TypeParams.WithDefaults<{ entity: TEntity }>
>[] {
  return [
    getIdcCategoryAttribute(getMapping),
    getIdcSubcategoryAttribute(getMapping),
  ]
}

export function getLabelAttributes<TEntity>(
  getMapping: (entity: TEntity) => ents.extra.rate.gl.Mapping | undefined,
): readonly ec.models.Attribute<
  ec.models.TypeParams.WithDefaults<{ entity: TEntity }>
>[] {
  return [
    getExclusionLabelAttribute(getMapping),
    getAdjustmentLabelAttribute(getMapping),
  ]
}

export function getAllAttributes<TEntity>(
  getMapping: (entity: TEntity) => ents.extra.rate.gl.Mapping | undefined,
): readonly ec.models.Attribute<
  ec.models.TypeParams.WithDefaults<{ entity: TEntity }>
>[] {
  return [
    ...getIdcCategorizationAttributes(getMapping),
    ...getLabelAttributes(getMapping),
  ]
}

/* --------------------------------- Helpers -------------------------------- */

export function getIdcCategoryValue(categorization: undefined): undefined
export function getIdcCategoryValue(
  categorization: ents.extra.common.FaFunction.Categorization.Identifiable,
): string
export function getIdcCategoryValue(
  categorization:
    | ents.extra.common.FaFunction.Categorization.Identifiable
    | undefined,
): string | undefined
export function getIdcCategoryValue(
  categorization:
    | ents.extra.common.FaFunction.Categorization.Identifiable
    | undefined,
): string | undefined {
  if (!categorization) {
    return undefined
  }
  const { category, categoryId } = categorization as FlattenUnion<
    typeof categorization
  >
  return category?.code ?? categoryId
}

export function getIdcSubcategoryValue(categorization: undefined): undefined
export function getIdcSubcategoryValue(
  categorization: ents.extra.common.FaFunction.Categorization.Identifiable,
): string
export function getIdcSubcategoryValue(
  categorization:
    | ents.extra.common.FaFunction.Categorization.Identifiable
    | undefined,
): string | undefined
export function getIdcSubcategoryValue(
  categorization:
    | ents.extra.common.FaFunction.Categorization.Identifiable
    | undefined,
): string | undefined {
  if (!categorization) {
    return undefined
  }
  const { subcategory, subcategoryId } = categorization as FlattenUnion<
    typeof categorization
  >
  return subcategory?.code ?? subcategoryId
}
